import { Button, Space, Spin, Table, Typography } from 'antd';
import { ColumnType, TableRowSelection } from 'antd/lib/table/interface';
import { CloudDownloadOutlined } from '@ant-design/icons';
import _ from 'lodash';
import React, { ReactElement, ReactNode, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import IApplication from '~Api/Application/IApplication';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import IBorrower from '~Api/Application/IBorrower';
import ICondition from '~Api/Application/ICondition';
import IConditionDocument from '~Api/Application/IConditionDocument';
import { IDictionary } from '~utilities/IDictionary';
import { IGlobalState } from '~reducer';
import {
    applicationConditionDocumentsDownloadAction,
    applicationConditionDocumentsListAction,
    applicationConditionsListAction,
    applicationGetAction,
} from '~Applications/actions';
import {
    applicationBorrowersSelector,
    applicationConditionDocumentsUpdatingSolicitorAccessSelector,
    applicationDownloadableConditionDocumentsByConditionUuidSelector,
    applicationEntityConditionsSelector,
    applicationPropertiesSelector,
    applicationSelector,
} from '~Applications/selectors';
import Layout from './Layout';

enum SolicitorDownloadsRowTypeEnum {
    Header = 'HEADER',
    Category = 'CATEGORY',
    File = 'FILE',
}

const bytesToKilobytes: (bytes: number) => number = (bytes: number) => {
    return bytes / 1024;
};

interface IRow {
    bytes: number;
    children?: IRow[];
    conditionDocument?: IConditionDocument;
    fileCount: number;
    label: string;
    rowType: SolicitorDownloadsRowTypeEnum;
    uid: string;
}

export default function SolicitorDownloads(): ReactElement {
    const { applicationUuid } = useParams<{applicationUuid: string}>();

    const [ selectedUuidKeys, setSelectedUuidKeys ] = useState<IDictionary<boolean>>(null);

    const application: IApplication = useSelector((state: IGlobalState) => applicationSelector(state, applicationUuid));
    const applicationProperties: IDictionary<IApplicationProperty> = useSelector((state: IGlobalState) => applicationPropertiesSelector(state, applicationUuid));
    const borrowers: IDictionary<IBorrower> = useSelector((state: IGlobalState) => applicationBorrowersSelector(state, applicationUuid));
    const conditionDocumentsByConditionUuid: IDictionary<IConditionDocument[]> = useSelector((state: IGlobalState) => applicationDownloadableConditionDocumentsByConditionUuidSelector(state, applicationUuid));
    const updatingSolicitorAccess: boolean = useSelector(applicationConditionDocumentsUpdatingSolicitorAccessSelector);
    const applicationEntityConditions: IDictionary<IDictionary<ICondition>> = useSelector((state: IGlobalState) => applicationEntityConditionsSelector(state, applicationUuid));
    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!application) {
            dispatch(applicationGetAction(applicationUuid));
        }
    }, [
        application,
        applicationUuid,
        dispatch,
    ]);

    useEffect(() => {
        if (!applicationEntityConditions) {
            dispatch(applicationConditionsListAction(applicationUuid));
        }
    }, [
        applicationEntityConditions,
        applicationUuid,
        dispatch,
    ]);

    useEffect(() => {
        if (!conditionDocumentsByConditionUuid) {
            dispatch(applicationConditionDocumentsListAction(applicationUuid));
        }
    }, [
        applicationUuid,
        conditionDocumentsByConditionUuid,
        dispatch,
    ]);

    const summaryRow: (data: readonly IRow[]) => JSX.Element = useCallback((data: readonly IRow[]) => {
        let totalFileCount: number = 0;
        let totalSize: number = 0;
        _.each(data, (row: IRow) => {
            totalFileCount += row.fileCount;
            totalSize += row.bytes;
        });

        return (
            <Table.Summary.Row>
                <Table.Summary.Cell index={0}>&nbsp;</Table.Summary.Cell>
                <Table.Summary.Cell index={1}>Total</Table.Summary.Cell>
                <Table.Summary.Cell index={2}>{totalFileCount}</Table.Summary.Cell>
                <Table.Summary.Cell index={3}>{_.round(bytesToKilobytes(totalSize))}</Table.Summary.Cell>
            </Table.Summary.Row>
        );
    }, []);

    const onChangeSelect: (keys: React.Key[], selectedRows: IRow[]) => void = useCallback((keys: React.Key[], selectedRows: IRow[]) => {
        const selectedUuidKeys: IDictionary<boolean> = {};
        _.each(selectedRows, (row: IRow) => {
            if (row.rowType !== SolicitorDownloadsRowTypeEnum.File) {
                return;
            }

            selectedUuidKeys[row.conditionDocument.uuid] = true;
        });

        setSelectedUuidKeys(selectedUuidKeys);
    }, []);

    let selectedRowKeys: IDictionary<boolean> = {};

    if (null !== selectedUuidKeys) {
        selectedRowKeys = selectedUuidKeys;
    } else {
        _.each(conditionDocumentsByConditionUuid, (conditionDocuments: IConditionDocument[]) => {
            _.each(conditionDocuments, (conditionDocument: IConditionDocument) => {
                if (conditionDocument.solicitorAccess) {
                    selectedRowKeys[conditionDocument.uuid] = true;
                }
            });
        });
    }

    const onClickDownload: () => void = useCallback(() => {
        dispatch(applicationConditionDocumentsDownloadAction(applicationUuid, _.keys(selectedRowKeys)));
    }, [
        applicationUuid,
        dispatch,
        selectedRowKeys,
    ]);

    if (!application || !applicationProperties || !borrowers || !conditionDocumentsByConditionUuid || !applicationEntityConditions) {
        return (
            <Layout applicationUuid={applicationUuid} section='solitor-downloads'>
                <Typography.Title level={2}>Solicitor Downloads</Typography.Title>
                <Spin/>
            </Layout>
        );
    }

    const parentRows: IRow[] = [];

    const addChildren: (entityUuid: string) => IRow[] = (entityUuid: string) => {
        const children: IRow[] = [];

        if (applicationEntityConditions[entityUuid]) {
            _.each(applicationEntityConditions[entityUuid], (condition: ICondition) => {
                if (!conditionDocumentsByConditionUuid[condition.uuid]) {
                    return;
                }
                const conditionDocumentRow: IRow = {
                    bytes: 0,
                    children: [],
                    fileCount: 0,
                    label: condition.name,
                    rowType: SolicitorDownloadsRowTypeEnum.Category,
                    uid: condition.uuid,
                };

                _.each(conditionDocumentsByConditionUuid[condition.uuid], (conditionDocument: IConditionDocument) => {
                    conditionDocumentRow.children.push({
                        bytes: conditionDocument.document.bytes,
                        conditionDocument: conditionDocument,
                        fileCount: 1,
                        label: conditionDocument.document.filename,
                        rowType: SolicitorDownloadsRowTypeEnum.File,
                        uid: conditionDocument.uuid,
                    });

                    if (selectedRowKeys[conditionDocument.uuid]) {
                        conditionDocumentRow.bytes += conditionDocument.document.bytes;
                        conditionDocumentRow.fileCount += 1;
                    }
                });

                children.push(conditionDocumentRow);
            });
        }

        return children;
    };

    const applicationParentRow: IRow = {
        bytes: 0,
        children: addChildren(application.uuid),
        fileCount: 0,
        label: 'Application',
        rowType: SolicitorDownloadsRowTypeEnum.Header,
        uid: application.uuid,
    };

    parentRows.push(applicationParentRow);

    _.each(borrowers, (borrower: IBorrower) => {
        const borrowerParentRow: IRow = {
            bytes: 0,
            children: addChildren(borrower.uuid),
            fileCount: 0,
            label: borrower.dealBorrower.formattedName,
            rowType: SolicitorDownloadsRowTypeEnum.Header,
            uid: borrower.uuid,
        };

        parentRows.push(borrowerParentRow);
    });

    _.each(applicationProperties, (applicationProperty: IApplicationProperty) => {
        const applicationPropertyParentRow: IRow = {
            bytes: 0,
            children: addChildren(applicationProperty.uuid),
            fileCount: 0,
            label: applicationProperty.dealProperty.streetAddress,
            rowType: SolicitorDownloadsRowTypeEnum.Header,
            uid: applicationProperty.uuid,
        };

        parentRows.push(applicationPropertyParentRow);
    });

    _.each(parentRows, (parentRow: IRow) => {
        _.each(parentRow.children, (conditionDocumentRow: IRow) => {
            parentRow.bytes += conditionDocumentRow.bytes;
            parentRow.fileCount += conditionDocumentRow.fileCount;
        });
    });

    const columns: ColumnType<IRow>[] = [
        {
            dataIndex: 'label',
            render: (label: string, row: IRow): ReactNode => {
                if (row.rowType !== SolicitorDownloadsRowTypeEnum.File) {
                    return label;
                }

                return (
                    <Space>
                        <span>{label}</span>
                        <Button
                            href={`${process.env.API_HOST}/deal-documents/${row.conditionDocument.documentUuid}/download`}
                            target='_blank'
                            type='link'
                            className='document approved'
                        >
                            <CloudDownloadOutlined/>
                        </Button>
                    </Space>
                );
            },
            title: 'Condition',
        },
        {
            dataIndex: 'fileCount',
            sorter: (a: IRow, b: IRow) => a.fileCount - b.fileCount,
            title: 'File Count',
            width: '20%',
        },
        {
            dataIndex: 'bytes',
            render: (bytes: number) => _.round(bytesToKilobytes(bytes)),
            sorter: (a: IRow, b: IRow) => a.bytes - b.bytes,
            title: 'File Size (KB)',
            width: '20%',
        },
    ];

    const rowSelection: TableRowSelection<IRow> = {
        checkStrictly: false,
        onChange: onChangeSelect,
        selectedRowKeys: _.keys(selectedRowKeys),
    };

    return (
        <Layout applicationUuid={applicationUuid} section='solicitor-downloads'>
            <Typography.Title level={2}>Solicitor Downloads</Typography.Title>
            <Table
                columns={columns}
                dataSource={parentRows}
                pagination={false}
                rowKey='uid'
                rowSelection={rowSelection}
                scroll={{ x: 1, y: 800 }}
                size='middle'
                summary={summaryRow}
            />
            <Button className='download-button' loading={updatingSolicitorAccess} onClick={onClickDownload}>
                Download Documents
            </Button>
        </Layout>
    );
}
